import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { prodApi, num, errMsg, fmtDate } from "./prodApi";
import { useProd } from "./ProdApp";
import { PButton, PInput, PSelect, PField, PTitle, PCard, PTextarea, PBadge, Spinner } from "./ProdUi";
import { OutputsEditor, CostsEditor, pctTotal } from "./ProdRecipeOutputs";

const S = (v, d = "") => (v == null ? d : String(v));
const EMPTY = { name: "", input_node_id: "", description: "", trim_level_id: "", labour: { minutes: "", basis: "per_piece", operators: "1", hourly_cost: "22" }, materials: [], other_costs: [], outputs: [] };

function toForm(r) {
  return {
    name: r.name, input_node_id: r.input_node_id, description: r.description || "", trim_level_id: r.trim_level_id || "",
    labour: { minutes: S(r.labour?.minutes), basis: r.labour?.basis || "per_piece", operators: S(r.labour?.operators, "1"), hourly_cost: S(r.labour?.hourly_cost) },
    materials: (r.materials || []).map((m) => ({ ...m, unit_cost: S(m.unit_cost), qty: S(m.qty) })),
    other_costs: (r.other_costs || []).map((c) => ({ ...c, amount: S(c.amount) })),
    outputs: (r.outputs || []).map((o) => ({ node_id: o.node_id, trim_level_id: o.trim_level_id || "", pct: S(o.pct), coefficient: S(o.coefficient), disposal: o.disposal, value_per_kg: S(o.value_per_kg), sale_price_per_kg: S(o.sale_price_per_kg) })),
  };
}

function toBody(f) {
  return {
    ...f, trim_level_id: f.trim_level_id || null,
    labour: { minutes: num(f.labour.minutes), basis: f.labour.basis, operators: num(f.labour.operators) || 1, hourly_cost: num(f.labour.hourly_cost) },
    materials: f.materials.map((m) => ({ ...m, unit_cost: num(m.unit_cost), qty: num(m.qty) })),
    other_costs: f.other_costs.map((c) => ({ ...c, amount: num(c.amount) })),
    outputs: f.outputs.map((o) => ({ ...o, trim_level_id: o.trim_level_id || null, pct: num(o.pct), coefficient: num(o.coefficient), value_per_kg: num(o.value_per_kg), sale_price_per_kg: num(o.sale_price_per_kg) })),
  };
}

export default function ProdRecipeEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { nodes, trims, user, reload } = useProd();
  const [f, setF] = useState(id ? null : EMPTY);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const canEdit = !!user?.is_manager;

  useEffect(() => {
    if (!id) { setF(EMPTY); setMeta(null); return; }
    prodApi.get(`/production/recipes/${id}`).then((r) => { setF(toForm(r.data)); setMeta(r.data); }).catch((e) => { toast.error(errMsg(e, "Ricetta non trovata")); navigate("/produzione/ricette"); });
  }, [id, navigate]);

  if (!f) return <Spinner text="Caricamento ricetta..." />;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const total = pctTotal(f.outputs);
  const balanced = Math.abs(total - 100) < 0.005;

  const save = async () => {
    if (!f.name.trim() || !f.input_node_id) { toast.error("Nome e materia prima di partenza sono obbligatori"); return; }
    if (!f.outputs.length) { toast.error("Aggiungi almeno un output"); return; }
    if (f.outputs.some((o) => !o.node_id)) { toast.error("Ogni riga di output deve avere un taglio"); return; }
    if (!balanced && !window.confirm(`La somma delle rese è ${total.toFixed(2)}% invece di 100%. Salvare comunque? (la ricetta resterà segnalata come non riconciliata)`)) return;
    setBusy(true);
    try {
      const body = toBody(f);
      const r = id ? await prodApi.put(`/production/recipes/${id}`, body) : await prodApi.post("/production/recipes", body);
      await reload();
      toast.success(id ? `Ricetta salvata (versione ${r.data.version})` : "Ricetta creata");
      if (!id) navigate(`/produzione/ricette/${r.data.id}`, { replace: true }); else setMeta((m) => ({ ...m, ...r.data }));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const inputNodes = nodes.filter((n) => n.active !== false && n.kind !== "waste" && n.kind !== "byproduct");
  return (
    <div className="space-y-5 pb-28" data-testid="prod-recipe-editor">
      <PTitle kicker={meta ? `${meta.code} · versione ${meta.version}` : "Nuova ricetta"} title={f.name || "Ricetta di lavorazione"}
        right={<>
          <PButton onClick={() => navigate("/produzione/ricette")} data-testid="prod-recipe-back">← Ricette</PButton>
          {meta && <PButton variant="primary" onClick={() => navigate(`/produzione/calcolatore?recipe=${meta.id}`)} data-testid="prod-recipe-to-calc">Simula</PButton>}
        </>}>
        {meta ? `Ultima modifica ${fmtDate(meta.updated_at)} · ${meta.updated_by || ""}` : "Definisci materia prima, output con resa teorica e costi di lavorazione."}
      </PTitle>

      <PCard className="grid md:grid-cols-2 gap-3">
        <PField label="Nome ricetta"><PInput value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Disosso completo" disabled={!canEdit} data-testid="prod-recipe-name" /></PField>
        <PField label="Materia prima di partenza">
          <PSelect value={f.input_node_id} onChange={(e) => set("input_node_id", e.target.value)} disabled={!canEdit} data-testid="prod-recipe-input-node">
            <option value="">— Scegli —</option>
            {inputNodes.map((n) => <option key={n.id} value={n.id}>{n.name} ({n.code})</option>)}
          </PSelect>
        </PField>
        <PField label="Livello di rifilatura predefinito">
          <PSelect value={f.trim_level_id} onChange={(e) => set("trim_level_id", e.target.value)} disabled={!canEdit} data-testid="prod-recipe-trim">
            <option value="">— Nessuno —</option>
            {trims.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </PSelect>
        </PField>
        <PField label="Descrizione"><PTextarea value={f.description} onChange={(e) => set("description", e.target.value)} disabled={!canEdit} className="!min-h-[56px]" data-testid="prod-recipe-description" /></PField>
      </PCard>

      <OutputsEditor outputs={f.outputs} onChange={(o) => set("outputs", o)} canEdit={canEdit} />
      <CostsEditor labour={f.labour} materials={f.materials} otherCosts={f.other_costs} onChange={(k, v) => set(k, v)} canEdit={canEdit} />

      {meta?.versions?.length > 0 && (
        <PCard>
          <h3 className="font-['Chivo'] font-black text-white mb-2">Versioni precedenti</h3>
          <div className="flex flex-wrap gap-2">
            {meta.versions.map((v) => <PBadge key={v.version} tone="slate" data-testid={`prod-recipe-version-${v.version}`}>v{v.version} · {fmtDate(v.saved_at)} · {v.saved_by}</PBadge>)}
          </div>
          <p className="text-xs text-slate-500 mt-2">Le simulazioni e lavorazioni salvate conservano la versione usata: non cambiano quando la ricetta viene aggiornata.</p>
        </PCard>
      )}

      {canEdit && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-slate-900/95 backdrop-blur border-t border-slate-700 p-3">
          <div className="max-w-[1500px] mx-auto flex items-center justify-between gap-3">
            <div className="text-sm">
              <span className="text-slate-400">Somma rese </span>
              <span className={`font-['Chivo'] font-black text-xl tabular-nums ${balanced ? "text-emerald-400" : "text-amber-400"}`} data-testid="prod-recipe-pct-total">{total.toFixed(2)}%</span>
              {!balanced && <span className="text-amber-400 ml-2">({(100 - total).toFixed(2)}% {total < 100 ? "mancanti" : "in eccesso"})</span>}
            </div>
            <PButton variant="primary" size="lg" onClick={save} disabled={busy} data-testid="prod-recipe-save">{id ? "Salva nuova versione" : "Crea ricetta"}</PButton>
          </div>
        </div>
      )}
    </div>
  );
}
